import { noItx } from "../../../src/translationItx/itx";
import BaseModel from "../../../src/models/BaseModel";
import BaseModelMap from "../../../src/models/BaseModelMap";
import ObjectFactory from "../../../src/models/ObjectFactory";
import type {
  C1_DefinitionItemData,
  C1_DefinitionItemInput,
  C1_EloItem_Definition,
  C1_LocalizedDefinitionText,
} from "../types/C1_DefinitionTypes";
import { C1_DefinitionItemTreeViewModel } from "./C1_DefinitionTreeViewModels";

type DefinitionProperties = Omit<C1_DefinitionItemData, "id" | "definitionId" | "children">;
type DefinitionInput = Partial<C1_DefinitionItemInput> & {
  definitionProperties?: DefinitionProperties;
};

const EMPTY_DESCRIPTION: C1_LocalizedDefinitionText = { de: "", en: "" };

/**
 * One expanded node of the non-persistent ELO definition catalog.
 *
 * `definitionId` identifies the reusable source entry in the JSON catalog.
 * `id` is different: it is the unique expanded path of this concrete catalog
 * node, for example `room/room.heating/heating.valve`. That path permits normal
 * BaseModel registration even when one source definition occurs in many trees.
 * `children` owns independently expanded definition nodes; no child object is
 * shared between two definition hierarchies.
 */
export class C1_DefinitionItem extends BaseModel {
  /** Original reusable key from C1_EloItemCatalog.json. */
  readonly definitionId: string;
  /** Definition values copied from the catalog entry, excluding identity and children. */
  definitionProperties: DefinitionProperties;
  /** Independently expanded child definitions owned by this hierarchy node. */
  readonly children: BaseModelMap<C1_DefinitionItem>;

  constructor(data: DefinitionInput = {}, parent?: BaseModel) {
    const { definitionId, children, definitionProperties, ...plainProperties } = data;

    super({ isPersistent: false }, parent);

    this.children = new BaseModelMap<C1_DefinitionItem>(C1_DefinitionItem, this, "children", false);
    this.treeViewModel = new C1_DefinitionItemTreeViewModel(this);
    this.definitionId = definitionId ?? "";

    const source = definitionProperties ?? plainProperties;
    this.definitionProperties = {
      ...source,
      type: source.type!,
      description: source.description ?? EMPTY_DESCRIPTION,
    };
    const parentDefinition = parent?.parent;

    if (parentDefinition instanceof C1_DefinitionItem) {
      this.id = `${parentDefinition.id}/${this.definitionId}`;
    } else {
      this.id = this.definitionId;
    }

    const childData = children ?? [];

    const childDefinitionIds = new Set<string>();
    for (const child of childData) {
      const childDefinitionId = child.definitionId;
      if (childDefinitionIds.has(childDefinitionId)) {
        console.error(noItx(`[C1 definitions] Duplicate child definitionId "${childDefinitionId}" below "${this.id}".`));
        continue;
      }
      childDefinitionIds.add(childDefinitionId);

      this.children.add(new C1_DefinitionItem(child, this.children));
    }
  }

  override get className(): string {
    return "C1_DefinitionItem";
  }

  /** Uses the catalog definition ID as its traceable navigator tag. */
  override getTag(): string {
    return this.definitionId;
  }

  override getPersistentProperties(): string[] {
    return ["definitionProperties", "children", ...super.getPersistentProperties()];
  }

  get type() {
    return this.definitionProperties.type;
  }

  get material() {
    return this.definitionProperties.material;
  }

  /** Shared furniture-definition ID used to resolve the catalog icon. */
  get icon() {
    return this.definitionProperties.icon;
  }

  get description() {
    return this.definitionProperties.description;
  }

  get descriptionShort() {
    return this.definitionProperties.descriptionShort;
  }

  get displayPosition() {
    return this.definitionProperties.displayPosition;
  }

  get usageCategory() {
    return this.definitionProperties.usageCategory;
  }

  get hasTag() {
    return this.definitionProperties.hasTag;
  }

  get tagSuffix() {
    return this.definitionProperties.tagSuffix;
  }

  get repeatIndex() {
    return this.definitionProperties.repeatIndex;
  }

  get defaultDetails() {
    return this.definitionProperties.defaultDetails;
  }

  get repeatDefaults() {
    return this.definitionProperties.repeatDefaults;
  }

  get defaultEnabled() {
    return this.definitionProperties.defaultEnabled;
  }

  get allways() {
    return this.definitionProperties.allways;
  }

  get planView() {
    return this.definitionProperties.planView;
  }

  get details() {
    return this.definitionProperties.details;
  }

  get position() {
    return this.definitionProperties.position;
  }

  get repeatDescription() {
    return this.definitionProperties.repeatDescription;
  }

  get descriptionTemplate() {
    return this.definitionProperties.descriptionTemplate;
  }

  get defaultName() {
    return this.definitionProperties.defaultName;
  }

  get generatedNameAliases() {
    return this.definitionProperties.generatedNameAliases;
  }

  get masterIndexPosition() {
    return this.definitionProperties.masterIndexPosition;
  }

  get includeInDescription() {
    return this.definitionProperties.includeInDescription;
  }

  /** Returns the legacy plain catalog representation used by current consumers. */
  toDefinition(): C1_EloItem_Definition {
    const items: C1_EloItem_Definition[] = [];
    for (const child of this.children) items.push(child.toDefinition());
    return {
      id: this.definitionId,
      ...this.definitionProperties,
      items: items.length > 0 ? items : undefined,
    };
  }
}

ObjectFactory.registerClass("C1_DefinitionItem", (data, parent) => new C1_DefinitionItem(data, parent));
